"use client";

import { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { ICONS } from "@artha/icons";
import { ShareBottomSheet, type ShareOption } from "@artha/ui";
import { useCreatorAnalytics } from "@/lib/analytics";
import {
  createSharePlatformOptions,
  type SharePlatformId,
  type SharePlatformOption,
} from "./sharePlatformOptions";

type ShareJobBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  shareText: string;
  xShareText?: string;
  jobTitle: string;
  companyName?: string | null;
  companyLogoUrl?: string | null;
  jobId?: string;
  jobCpaValue?: number;
  jobCpcValue?: number;
  creatorName?: string | null;
  creatorAvatarUrl?: string | null;
  creatorUsername?: string | null;
  title?: string | null;
  disableJobAnalytics?: boolean;
  onNativeShare?: (platform: SharePlatformId) => void;
  onCopyLink?: () => void;
  onPlatformShare?: (platform: Exclude<SharePlatformId, "copy-link">) => void;
};

const mapShareOption = (option: SharePlatformOption): ShareOption => ({
  id: option.key,
  label: option.label,
  icon: option.icon,
  className: option.iconWrapperClassName,
  onClick: option.onClick,
});

export const ShareJobBottomSheet = ({
  isOpen,
  onClose,
  shareUrl,
  shareText,
  xShareText,
  jobTitle,
  companyName,
  companyLogoUrl,
  jobId,
  jobCpaValue,
  jobCpcValue,
  creatorName,
  creatorAvatarUrl,
  creatorUsername,
  title,
  disableJobAnalytics = false,
  onNativeShare,
  onCopyLink,
  onPlatformShare,
}: ShareJobBottomSheetProps) => {
  const t = useTranslations("shareJob");
  const { trackJobShare } = useCreatorAnalytics();

  const trackShare = useCallback(
    (socialMedia: string) => {
      trackJobShare(
        {
          social_media: socialMedia,
          job_id: jobId || "",
          job_title: jobTitle,
          job_company: companyName || "",
          job_cpa_value: jobCpaValue,
          job_cpc_value: jobCpcValue,
        },
        creatorUsername ? { username: creatorUsername } : undefined
      );
    },
    [companyName, creatorUsername, jobCpaValue, jobCpcValue, jobId, jobTitle, trackJobShare]
  );

  const shareOptions = useMemo<ShareOption[]>(
    () =>
      createSharePlatformOptions({
        labels: {
          tiktok: t("tiktokLabel"),
          facebook: t("facebookLabel"),
          x: t("xLabel"),
          whatsapp: t("whatsappLabel"),
          threads: t("threadsLabel"),
        },
        shareUrl,
        shareText,
        xShareText,
        shareTextWithUrl: shareUrl ? `${shareText.replace(shareUrl, "").trim()} ${shareUrl}`.trim() : shareText,
        onNativeShare,
        onTrack: disableJobAnalytics ? undefined : trackShare,
        onPlatformShare,
        onClose,
      })
        .filter((option) => option.key !== "copy-link")
        .map(mapShareOption),
    [disableJobAnalytics, onClose, onNativeShare, onPlatformShare, shareText, shareUrl, t, trackShare, xShareText]
  );

  const displayCreator = creatorName || "Artha";
  const displayCompany = companyName || "Unknown company";
  const creatorInitial = displayCreator.charAt(0).toUpperCase();
  const companyInitial = displayCompany.charAt(0).toUpperCase();

  const previewCard = (
    <div className="relative overflow-hidden rounded-[8px] border border-black bg-[#ED5AA9] px-4 py-5 shadow-[0px_4px_0px_0px_black]">
      <div className="pointer-events-none absolute right-[-24px] top-[-36px] h-[132px] w-[170px] opacity-70">
        <WavePattern className="h-full w-full text-[#F377BE]" />
      </div>
      <div className="relative z-10 flex flex-col items-center justify-between space-y-5 text-center text-white">
        <div className="flex flex-col items-center gap-2">
          <div className="flex justify-center pb-1">
            <ICONS.ARTHA_LINK_SMALL className="h-4 w-4" color="#fff" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 overflow-hidden rounded-full bg-white/20">
              {companyLogoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={companyLogoUrl}
                  alt={displayCompany}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold uppercase text-white">
                  {companyInitial}
                </span>
              )}
            </div>
            <span className="text-sm font-normal leading-[20px] text-[#F1F1F1]">
              {displayCompany}
            </span>
          </div>
          <div className="max-w-[259px]">
            <p className="text-xl font-semibold text-white">{jobTitle}</p>
          </div>
        </div>

        <div className="mb-2 flex items-center gap-1.5">
          <span className="text-[8px] font-medium text-[#F1F1F1]">
            {t("sharedByLabel")}
          </span>
          <div className="h-3 w-3 overflow-hidden rounded-full bg-white/20">
            {creatorAvatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={creatorAvatarUrl}
                alt={displayCreator}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[6px] font-semibold uppercase text-white">
                {creatorInitial}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ShareBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      profileName={displayCreator}
      profileHandle={creatorUsername ? `@${creatorUsername}` : ""}
      profileAvatar={creatorAvatarUrl || undefined}
      shareLink={shareUrl}
      shareOptions={shareOptions}
      linkedPlatforms={[]}
      onCopyLink={onCopyLink}
      title={title ?? undefined}
      hideEditButton={true}
      hideSettings={true}
      previewCard={previewCard}
      className="bg-[#FFF9EC] px-3 pb-4 pt-3 shadow-[0_-12px_76px_rgba(0,0,0,0.16)]"
    />
  );
};

const WavePattern = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 180 140" fill="none" className={className} aria-hidden="true">
    <path
      d="M10 30C32 12 56 12 78 30C100 48 124 48 146 30C160 18 171 14 178 12"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      opacity="0.5"
    />
    <path
      d="M8 62C30 44 54 44 76 62C98 80 122 80 144 62C158 50 170 46 178 44"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      opacity="0.35"
    />
    <path
      d="M6 94C28 76 52 76 74 94C96 112 120 112 142 94C156 82 168 78 176 76"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      opacity="0.2"
    />
  </svg>
);
